// InvestmentList.tsx - Comprehensive investment listing with CRUD operations

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Investment, 
  InvestmentFilters, 
  InvestmentSortOptions,
  InvestmentBulkOperation 
} from '../../types/Investment';
import { ScenarioInvestment } from '../../types/ScenarioManagement';
import { InvestmentService } from '../../services/InvestmentService';
import { useToast } from '../../hooks/useToast';
import { useTheme } from '../../contexts/ThemeContext';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { logger } from '../../services/LoggingService';
import { StandardButton } from '../ui/StandardButton';
import { InvestmentGridCard } from './InvestmentGridCard';
import { InvestmentTableRow } from './InvestmentTableRow';

interface InvestmentListProps {
  investments?: ScenarioInvestment[];
  onEdit: (investment: Investment) => void;
  onToggle?: (investmentId: string, enabled: boolean) => void;
  onDelete?: (investmentId: string) => void;
  refreshTrigger?: number;
}

export const InvestmentList: React.FC<InvestmentListProps> = ({
  investments: propInvestments,
  onEdit,
  onToggle,
  onDelete,
  refreshTrigger = 0
}) => {
  const { showToast } = useToast();
  const { isDark } = useTheme();
  const [investments, setInvestments] = useState<Investment[]>([]);
  const useScenarioInvestments = !!propInvestments;
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [filters, setFilters] = useState<InvestmentFilters>({});
  const [sort] = useState<InvestmentSortOptions>({ field: 'name', direction: 'asc' });
  // Removed unused expandedInvestments state
  const [pagination, setPagination] = useState({ page: 1, limit: 12, total: 0 });
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');

  const loadInvestments = useCallback(async () => {
    if (propInvestments && propInvestments.length > 0) {
      // Use scenario investments
      setLoading(true);
      
      // Convert ScenarioInvestment to Investment for display
      const convertedInvestments: Investment[] = propInvestments.map(inv => ({
        id: inv.id,
        name: inv.name,
        icon: inv.icon,
        description: inv.description,
        cost: inv.cost,
        impact: inv.impact,
        category: inv.category as Investment['category'],
        businessOutcomes: inv.businessOutcomes,
        isActive: inv.enabled !== false,
        tags: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }));
      
      // Apply filters
      let filtered = convertedInvestments;
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        filtered = filtered.filter(inv => 
          inv.name.toLowerCase().includes(searchLower) ||
          inv.description.toLowerCase().includes(searchLower)
        );
      }
      if (filters.category) {
        filtered = filtered.filter(inv => inv.category === filters.category);
      }
      if (filters.riskLevel) {
        filtered = filtered.filter(inv => inv.riskLevel === filters.riskLevel);
      }
      if (filters.costMax !== undefined && filters.costMax !== null) {
        const maxCost = filters.costMax;
        filtered = filtered.filter(inv => inv.cost <= maxCost);
      }
      
      setInvestments(filtered);
      setPagination(prev => ({ ...prev, total: filtered.length }));
      setLoading(false);
    } else {
      // Load from service
      try {
        setLoading(true);
        const response = await InvestmentService.getAll(filters, sort, pagination);
        setInvestments(response.data);
        setPagination(prev => ({ ...prev, total: response.total }));
      } catch (error) {
        showToast({ title: 'Error loading investments', type: 'error' });
        logger.error('Error loading investments', 'InvestmentList', {}, error as Error);
      } finally {
        setLoading(false);
      }
    }
  }, [propInvestments, filters, sort, showToast, pagination]);

  useEffect(() => {
    loadInvestments();
  }, [loadInvestments, refreshTrigger]);

  const handleDelete = async (id: string) => {
    if (useScenarioInvestments && onDelete) {
      onDelete(id);
    } else {
      try {
        await InvestmentService.delete(id);
        showToast({ title: 'Investment deleted successfully', type: 'success' });
        loadInvestments();
      } catch (error) {
        showToast({ title: 'Error deleting investment', type: 'error' });
      }
    }
  };

  const handleBulkOperation = async (operation: InvestmentBulkOperation) => {
    try {
      const result = await InvestmentService.bulkOperation(operation);
      showToast({
        title: `Bulk operation completed: ${result.success} successful, ${result.failed} failed`,
        type: result.failed === 0 ? 'success' : 'warning'
      });
      if (result.errors.length > 0) {
        logger.error('Bulk operation errors occurred', 'InvestmentList', {
          operation: operation.operation,
          errorCount: result.errors.length,
          errors: result.errors
        });
      }
      setSelectedIds([]);
      loadInvestments();
    } catch (error) {
      showToast({ title: 'Error performing bulk operation', type: 'error' });
    }
  };

  const toggleSelection = (id: string) => {
    setSelectedIds(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === investments.length && investments.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(investments.map(inv => inv.id));
    }
  };

  // Removed unused toggleExpanded function

  const formatCost = (cost: number) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0 
    }).format(cost);
  };

  // Removed unused getCategoryIcon function

  // Removed unused getRiskLevelColor function

  // Removed unused getImpactIcon function

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header - Simplified for tab context */}
      {!propInvestments && (
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Investment Management</h2>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              isDark ? 'bg-professional-blue-600/20 text-professional-blue-400' : 'bg-blue-100 text-blue-800'
            }`}>
              {pagination.total} total
            </span>
          </div>
          
          {/* View Mode Toggle */}
          <div className={`flex ${isDark ? 'bg-white/[0.05]' : 'bg-gray-100'} rounded-lg p-1`}>
            <StandardButton
              type="button"
              onClick={() => setViewMode('grid')}
              variant={viewMode === 'grid' ? 'primary' : 'ghost'}
              size="small"
              title="Grid View"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M4 8h4V4H4v4zm6 12h4v-4h-4v4zm-6 0h4v-4H4v4zm0-6h4v-4H4v4zm6 0h4v-4h-4v4zm6-10v4h4V4h-4zm-6 4h4V4h-4v4zm6 6h4v-4h-4v4zm0 6h4v-4h-4v4z"/>
              </svg>
            </StandardButton>
            <StandardButton
              type="button"
              onClick={() => setViewMode('table')}
              variant={viewMode === 'table' ? 'primary' : 'ghost'}
              size="small"
              title="Table View"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 3h18v2H3V3zm0 4h18v2H3V7zm0 4h18v2H3v-2zm0 4h18v2H3v-2zm0 4h18v2H3v-2z"/>
              </svg>
            </StandardButton>
          </div>
        </div>
      )}

      {/* Simplified Filters for tab context */}
      {propInvestments && (
        <div className="flex items-center justify-between mb-4">
          <input
            type="text"
            value={filters.search || ''}
            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            placeholder="Search investments..."
            className={`flex-1 max-w-md px-3 py-2 rounded-lg focus:outline-none focus:ring-2 ${
              isDark 
                ? 'bg-white/[0.05] border-white/10 text-white placeholder-slate-400 focus:ring-professional-blue-400' 
                : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-500 focus:ring-blue-500'
            } border`}
          />
          
          {/* View Mode Toggle for scenario context */}
          <div className={`flex ${isDark ? 'bg-white/[0.05]' : 'bg-gray-100'} rounded-lg p-1`}>
            <StandardButton
              type="button"
              onClick={() => setViewMode('grid')}
              variant={viewMode === 'grid' ? 'primary' : 'ghost'}
              size="small"
              title="Grid View"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M4 8h4V4H4v4zm6 12h4v-4h-4v4zm-6 0h4v-4H4v4zm0-6h4v-4H4v4zm6 0h4v-4h-4v4zm6-10v4h4V4h-4zm-6 4h4V4h-4v4zm6 6h4v-4h-4v4zm0 6h4v-4h-4v4z"/>
              </svg>
            </StandardButton>
            <StandardButton
              type="button"
              onClick={() => setViewMode('table')}
              variant={viewMode === 'table' ? 'primary' : 'ghost'}
              size="small"
              title="Table View"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 3h18v2H3V3zm0 4h18v2H3V7zm0 4h18v2H3v-2zm0 4h18v2H3v-2zm0 4h18v2H3v-2z"/>
              </svg>
            </StandardButton>
          </div>
        </div>
      )}

      {/* Bulk Actions */}
      {selectedIds.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`rounded-lg p-4 border ${
            isDark 
              ? 'bg-professional-blue-900/20 border-professional-blue-800/50' 
              : 'bg-blue-50 border-blue-200'
          }`}
        >
          <div className="flex items-center justify-between">
            <span className={`text-sm font-medium ${
              isDark ? 'text-professional-blue-300' : 'text-blue-900'
            }`}>
              {selectedIds.length} investment{selectedIds.length !== 1 ? 's' : ''} selected
            </span>
            <div className="flex gap-2">
              <StandardButton
                onClick={() => handleBulkOperation({ operation: 'activate', investmentIds: selectedIds })}
                variant="success"
                size="small"
              >
                Activate
              </StandardButton>
              <StandardButton
                onClick={() => handleBulkOperation({ operation: 'deactivate', investmentIds: selectedIds })}
                variant="secondary"
                size="small"
              >
                Deactivate
              </StandardButton>
              <StandardButton
                onClick={() => handleBulkOperation({ operation: 'delete', investmentIds: selectedIds })}
                variant="danger"
                size="small"
              >
                Delete
              </StandardButton>
            </div>
          </div>
        </motion.div>
      )}

      {/* Investment Grid/Table */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          <AnimatePresence mode="popLayout">
            {investments.map((investment) => (
              <InvestmentGridCard
                key={investment.id}
                investment={investment}
                isSelected={selectedIds.includes(investment.id)}
                onEdit={onEdit}
                onDelete={handleDelete}
                onToggle={onToggle}
                onToggleSelection={toggleSelection}
                useScenarioInvestments={useScenarioInvestments}
                formatCost={formatCost}
              />
            ))}
          </AnimatePresence>
        </div>
      ) : (
        /* Table View */
        <div className={`rounded-lg shadow-sm border overflow-hidden ${
          isDark 
            ? 'bg-white/[0.03] border-white/10' 
            : 'bg-white border-gray-200'
        }`}>
          <div className="overflow-x-auto">
            <table className={`min-w-full divide-y ${
              isDark ? 'divide-white/10' : 'divide-gray-200'
            }`}>
              <thead className={isDark ? 'bg-white/[0.02]' : 'bg-gray-50'}>
                <tr>
                  <th className="px-6 py-3 text-left">
                    {!useScenarioInvestments && (
                      <input
                        type="checkbox"
                        checked={selectedIds.length === investments.length && investments.length > 0}
                        onChange={toggleSelectAll}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        style={{ transform: 'scale(0.5)', transformOrigin: 'center' }}
                      />
                    )}
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    isDark ? 'text-slate-400' : 'text-gray-500'
                  }`}>
                    Investment
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    isDark ? 'text-slate-400' : 'text-gray-500'
                  }`}>
                    Cost
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                    isDark ? 'text-slate-400' : 'text-gray-500'
                  }`}>
                    Status
                  </th>
                  <th className={`px-6 py-3 text-right text-xs font-medium uppercase tracking-wider ${
                    isDark ? 'text-slate-400' : 'text-gray-500'
                  }`}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${
                isDark 
                  ? 'bg-white/[0.01] divide-white/10' 
                  : 'bg-white divide-gray-200'
              }`}>
                {investments.map((investment) => (
                  <InvestmentTableRow
                    key={investment.id}
                    investment={investment}
                    isSelected={selectedIds.includes(investment.id)}
                    onEdit={onEdit}
                    onDelete={handleDelete}
                    onToggle={onToggle}
                    onToggleSelection={toggleSelection}
                    useScenarioInvestments={useScenarioInvestments}
                    formatCost={formatCost}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {pagination.total > pagination.limit && (
        <div className="flex items-center justify-between">
          <div className={`text-sm ${
            isDark ? 'text-slate-300' : 'text-gray-700'
          }`}>
            Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
          </div>
          <div className="flex gap-2">
            <StandardButton
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              disabled={pagination.page === 1}
              variant="secondary"
              size="small"
            >
              Previous
            </StandardButton>
            <StandardButton
              onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              disabled={pagination.page * pagination.limit >= pagination.total}
              variant="secondary"
              size="small"
            >
              Next
            </StandardButton>
          </div>
        </div>
      )}

      {/* Empty State */}
      {investments.length === 0 && !loading && (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">📦</div>
          <h3 className={`text-lg font-medium mb-2 ${
            isDark ? 'text-white' : 'text-gray-900'
          }`}>No investments found</h3>
          <p className={isDark ? 'text-slate-300' : 'text-gray-500'}>
            {Object.keys(filters).length > 0 
              ? 'Try adjusting your filters or search terms'
              : 'Get started by creating your first investment'
            }
          </p>
        </div>
      )}
    </div>
  );
};